import React, {useEffect, useRef, useState} from "react";
import Lottie from "lottie-react";
import ClickAnimation from "../assets/json/ClickAnimation.json";

type Area = {
  shape: string;
  coords: string;
  alt: string;
  color?: string;
  className?: string;
};

type ResponsiveImageMapProps = {
  src: string;
  alt: string;
  mapName: string;
  areas: Area[];
};

const ResponsiveImageMapOrientation: React.FC<ResponsiveImageMapProps> = ({
  src,
  alt,
  mapName,
  areas,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [scaledAreas, setScaledAreas] = useState<Area[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null); // Track highlighted area index

  useEffect(() => {
    const resizeMap = () => {
      if (!imgRef.current) return;

      const img = imgRef.current;
      if (!img.naturalWidth || !img.naturalHeight) return;

      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      const currentWidth = img.offsetWidth;
      const currentHeight = img.offsetHeight;

      const xRatio = currentWidth / originalWidth;
      const yRatio = currentHeight / originalHeight;

      const updatedAreas = areas.map((area) => {
        const coords = area.coords.split(",").map(Number);
        const scaledCoords = coords.map((coord, index) =>
          index % 2 === 0 ? coord * xRatio : coord * yRatio
        );
        return {...area, coords: scaledCoords.join(",")};
      });

      setScaledAreas(updatedAreas);
    };

    const handleResize = () => resizeMap();

    window.addEventListener("resize", handleResize);

    const img = imgRef.current;
    if (img && img.complete) {
      resizeMap();
    } else {
      img?.addEventListener("load", resizeMap);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [areas]);

  const renderBackgrounds = () => {
    return scaledAreas.map((area, index) => {
      const coords = area.coords.split(",").map(Number);
      let style: React.CSSProperties = {};

      if (area.shape === "rect") {
        style = {
          left: `${coords[0]}px`,
          top: `${coords[1]}px`,
          width: `${coords[2] - coords[0]}px`,
          height: `${coords[3] - coords[1]}px`,
        };
      } else if (area.shape === "circle") {
        style = {
          left: `${coords[0] - coords[2]}px`,
          top: `${coords[1] - coords[2]}px`,
          width: `${coords[2] * 2}px`,
          height: `${coords[2] * 2}px`,
          borderRadius: "50%",
        };
      }

      const isHighlighted = index === highlightedIndex;

      return (
        <div
          key={index}
          className={`parent-modal-bubble-orientation ${area.alt}-hover-blk`}
          onMouseEnter={() => {
            if (index === 0) {
              let cursorPointer = document.getElementById("cursorPointer");
              if (cursorPointer) {
                cursorPointer.style.display = "none";
              }
            }
          }}
          style={{
            ...style,
            position: "absolute",
            backgroundColor: isHighlighted
              ? "rgba(0, 255, 0, 0.5)"
              : area.color || "rgba(255, 0, 0, 0.5)",
            cursor: "pointer",
          }}>
          {index === 0 && (
            <Lottie
              id="cursorPointer"
              animationData={ClickAnimation}
              className="click-animation-blk-orientation"
            />
          )}
          {area.alt === "dayone" && (
            <div
              className={`modal-bubble-orientation ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Day 1: Introduction to iCXeed Expert Essentials and our
                Innovation Values & DNA
                <br />
                <br />
                This day focuses on immersing new hires in our unique culture
                and core principles.
                <br />
                <br />
                We introduce our DNA and values, ensuring new experts understand
                what makes iCXeed different.
              </p>
            </div>
          )}

          {area.alt === "daytwothree" && (
            <div
              className={`modal-bubble-orientation ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Day 2-3: Deep dive into Business Process Innovation techniques
                and practices
              </p>
              <ul>
                <li>
                  <p>
                    These two days are dedicated to in-depth exploration of what
                    Business Process Innovation is and how diversity drives
                    innovation.
                  </p>
                </li>
                <li>
                  <p>
                    We cover key concepts, tools, and best practices that form
                    the foundation of our approach.
                  </p>
                </li>
                <li>
                  <p>
                    Hands-on exercises and case studies help reinforce learning
                    and practical application.
                  </p>
                </li>
              </ul>
            </div>
          )}

          {area.alt === "dayfour" && (
            <div
              className={`modal-bubble-orientation ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Day 4: Application of learnings to client-specific challenges
              </p>

              <ul>
                <li>
                  <p>
                    The final day bridges theory and practice by applying new
                    knowledge to real-world scenarios.
                  </p>
                </li>
                <li>
                  <p>
                    This helps new experts contextualize their learning and
                    prepares them for client engagements.
                  </p>
                </li>
              </ul>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div style={{position: "relative"}}>
      <img
        ref={imgRef}
        src={src}
        useMap={`#${mapName}`}
        alt={alt}
        style={{maxWidth: "100%", height: "auto", display: "block"}}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}>
        {renderBackgrounds()}
      </div>
    </div>
  );
};

export default ResponsiveImageMapOrientation;
